import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Bell, 
  Mail, 
  Settings, 
  CheckCircle,
  Loader2,
  ArrowLeft,
  FileText,
  Lightbulb,
  Calendar,
  Send,
  Eye,
  MapPin,
  Building2,
  Download,
  Trash2,
  Shield,
  AlertTriangle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
  const [preferences, setPreferences] = useState({
    weekly_digest: true,
    new_tenders: true,
    tips_and_guides: true
  });
  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchNewsletterPreferences();
  }, []);

  const fetchNewsletterPreferences = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/newsletter/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsletterSubscribed(response.data.subscribed);
      setPreferences(response.data.preferences || {
        weekly_digest: true,
        new_tenders: true,
        tips_and_guides: true
      });
    } catch (error) {
      console.error("Error fetching newsletter preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/newsletter/preferences`,
        {
          subscribed: newsletterSubscribed,
          preferences: preferences
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Préférences enregistrées");
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const fetchDigestPreview = async () => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/newsletter/preview-digest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDigestPreview(response.data);
    } catch (error) {
      console.error("Error fetching digest preview:", error);
      toast.error("Erreur lors du chargement de la prévisualisation");
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/api/newsletter/send-test-digest`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email envoyé à ${user?.email}`);
    } catch (error) {
      console.error("Error sending test digest:", error);
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(errorMsg);
    } finally {
      setSendingTest(false);
    }
  };

  // RGPD - Export user data
  const handleExportData = async () => {
    setExportingData(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/auth/export-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mes-donnees-dce-express-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Vos données ont été exportées avec succès");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Erreur lors de l'export des données");
    } finally {
      setExportingData(false);
    }
  };

  // RGPD - Delete account
  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Votre compte a été supprimé avec succès");
      logout();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Erreur lors de la suppression du compte");
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Settings className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-slate-900">
                Paramètres
              </h1>
              <p className="text-slate-600">
                Gérez vos préférences de notification et newsletter
              </p>
            </div>
          </div>
        </motion.div>

        {/* Newsletter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <CardTitle>Newsletter</CardTitle>
                  <CardDescription>
                    Configurez les emails que vous souhaitez recevoir
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Main toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-slate-600" />
                  <div>
                    <Label htmlFor="newsletter-main" className="font-medium">
                      Recevoir la newsletter
                    </Label>
                    <p className="text-sm text-slate-500">
                      Activez pour recevoir nos emails
                    </p>
                  </div>
                </div>
                <Switch
                  id="newsletter-main"
                  checked={newsletterSubscribed}
                  onCheckedChange={setNewsletterSubscribed}
                  data-testid="newsletter-toggle"
                />
              </div>

              {/* Preference options */}
              {newsletterSubscribed && (
                <div className="space-y-4 pl-4 border-l-2 border-violet-200">
                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      <div>
                        <Label htmlFor="weekly-digest" className="font-medium">
                          Récapitulatif hebdomadaire
                        </Label>
                        <p className="text-sm text-slate-500">
                          Résumé des meilleurs marchés de la semaine
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="weekly-digest"
                      checked={preferences.weekly_digest}
                      onCheckedChange={() => togglePreference('weekly_digest')}
                      data-testid="weekly-digest-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-emerald-500" />
                      <div>
                        <Label htmlFor="new-tenders" className="font-medium">
                          Nouveaux marchés
                        </Label>
                        <p className="text-sm text-slate-500">
                          Alertes pour les marchés correspondant à votre profil
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="new-tenders"
                      checked={preferences.new_tenders}
                      onCheckedChange={() => togglePreference('new_tenders')}
                      data-testid="new-tenders-toggle"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      <div>
                        <Label htmlFor="tips-guides" className="font-medium">
                          Conseils et guides
                        </Label>
                        <p className="text-sm text-slate-500">
                          Astuces pour remporter vos marchés publics
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="tips-guides"
                      checked={preferences.tips_and_guides}
                      onCheckedChange={() => togglePreference('tips_and_guides')}
                      data-testid="tips-guides-toggle"
                    />
                  </div>
                </div>
              )}

              {/* Save button */}
              <div className="pt-4">
                <Button
                  onClick={savePreferences}
                  disabled={saving}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  data-testid="save-preferences-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer les préférences
                </Button>
              </div>

              {/* Test Email Section */}
              {newsletterSubscribed && preferences.weekly_digest && (
                <div className="pt-6 border-t border-slate-200 mt-6">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">
                    Tester le récap hebdomadaire
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Prévisualisez le contenu de votre prochaine newsletter ou envoyez un email de test.
                  </p>
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={fetchDigestPreview}
                      disabled={loadingPreview}
                      className="flex-1"
                      data-testid="preview-digest-btn"
                    >
                      {loadingPreview ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Prévisualiser
                    </Button>
                    <Button
                      onClick={sendTestDigest}
                      disabled={sendingTest}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      data-testid="send-test-digest-btn"
                    >
                      {sendingTest ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Envoyer un test
                    </Button>
                  </div>

                  {/* Preview Display */}
                  {digestPreview && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-violet-100 text-violet-700">
                          {digestPreview.company_sector || "Secteur non défini"}
                        </Badge>
                        <Badge variant="outline" className="text-slate-600">
                          <MapPin className="w-3 h-3 mr-1" />
                          {digestPreview.company_region || "Région non définie"}
                        </Badge>
                      </div>
                      
                      <div className="bg-emerald-50 rounded-lg p-3 mb-3 border border-emerald-200">
                        <p className="text-xs font-semibold text-emerald-700 mb-1">
                          💡 Conseil de la semaine
                        </p>
                        <p className="text-sm font-medium text-slate-900">
                          {digestPreview.weekly_tip?.title}
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {digestPreview.weekly_tip?.content}
                        </p>
                      </div>

                      <div className="text-sm text-slate-700 mb-2">
                        <span className="font-medium">{digestPreview.relevant_tenders_count}</span> marchés pertinents trouvés
                      </div>

                      {digestPreview.sample_tenders?.length > 0 && (
                        <div className="space-y-2">
                          {digestPreview.sample_tenders.slice(0, 2).map((tender, idx) => (
                            <div key={`item-${idx}`} className="text-xs p-2 bg-white rounded border border-slate-100">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs py-0">
                                  {tender.source}
                                </Badge>
                                <span className="text-slate-400">•</span>
                                <span className="text-slate-500">{tender.sector}</span>
                              </div>
                              <p className="font-medium text-slate-800 line-clamp-1">
                                {tender.title}
                              </p>
                              <p className="text-slate-500 flex items-center gap-1 mt-1">
                                <Building2 className="w-3 h-3" />
                                {tender.buyer}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations du compte</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-600">Email</span>
                  <span className="font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-600">Nom</span>
                  <span className="font-medium">{user?.full_name}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600">Abonnement</span>
                  <span className="font-medium capitalize">{user?.subscription_tier || 'Gratuit'}</span>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate("/profile")}
                  className="flex-1"
                >
                  Modifier le profil
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/pricing")}
                  className="flex-1"
                >
                  Gérer l'abonnement
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* RGPD Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Shield className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-orange-900">Protection des Données (RGPD)</CardTitle>
                  <CardDescription className="text-orange-700">
                    Gérez vos droits sur vos données personnelles
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Export Data */}
              <div className="p-4 bg-white rounded-lg border border-orange-200">
                <div className="flex items-start gap-4">
                  <Download className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">Exporter mes données</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Téléchargez une copie de toutes vos données personnelles (profil, favoris, activités, etc.) au format JSON.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportData}
                      disabled={exportingData}
                      className="mt-3 border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      {exportingData ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Export en cours...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger mes données
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Delete Account */}
              <div className="p-4 bg-white rounded-lg border border-red-200">
                <div className="flex items-start gap-4">
                  <Trash2 className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">Supprimer mon compte</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Supprimez définitivement votre compte et toutes vos données personnelles. 
                      Cette action est <strong>irréversible</strong>.
                    </p>
                    
                    {!showDeleteConfirm ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="mt-3 border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer mon compte
                      </Button>
                    ) : (
                      <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 text-red-800 mb-3">
                          <AlertTriangle className="w-5 h-5" />
                          <span className="font-medium">Êtes-vous sûr ?</span>
                        </div>
                        <p className="text-sm text-red-700 mb-3">
                          Toutes vos données seront définitivement supprimées : profil, favoris, historique, abonnements newsletter...
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleDeleteAccount}
                            disabled={deletingAccount}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {deletingAccount ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Suppression...
                              </>
                            ) : (
                              "Oui, supprimer définitivement"
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deletingAccount}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
